import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Calendar, Clock, FileText, Plane } from 'lucide-react';
import { format } from 'date-fns';
import { LeaveRequestActions } from '@/components/leave/LeaveRequestActions';
import type { LeaveRequest } from '@/services/leave/types';
import { getLeaveTypeLabel } from '@/constants/leaveTypes';

const HOURS_PER_DAY = 8;

interface LeaveRequestCardProps {
  request: LeaveRequest;
  getStatusBadgeVariant: (status: string) => any;
  calculateDays: (startDate: string, endDate: string) => number;
}

export function LeaveRequestCard({
  request,
  getStatusBadgeVariant,
  calculateDays,
}: LeaveRequestCardProps) {
  const isPartDay = request.requested_hours !== null && 
                    request.requested_hours !== undefined && 
                    request.requested_hours > 0;

  const formatDuration = () => {
    if (isPartDay && request.requested_hours) {
      const days = request.requested_hours / HOURS_PER_DAY;
      return `${request.requested_hours}h (${days.toFixed(2)} days)`;
    }
    return `${calculateDays(request.start_date, request.end_date)} days`;
  };

  const formatTimeRange = () => {
    if (request.start_time && request.end_time) {
      return `${request.start_time.substring(0, 5)} - ${request.end_time.substring(0, 5)}`;
    }
    return null;
  };

  return (
    <Card className="w-full min-w-0">
      <CardContent className="p-4 overflow-hidden">
        <div className="space-y-3">
          {/* Leave Type */}
          <div className="flex items-center gap-2">
            <Plane className="h-4 w-4 text-muted-foreground" />
            <span className="font-semibold text-lg">{getLeaveTypeLabel(request.leave_type)}</span>
            {isPartDay && (
              <Badge variant="outline" className="text-xs">
                Part Day
              </Badge>
            )}
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <span className="text-sm text-muted-foreground">
                {isPartDay ? 'Date' : 'Start Date'}
              </span>
              <p className="font-medium">
                {format(new Date(request.start_date), 'MMM d, yyyy')}
              </p>
              {/* Show time range for part-day */}
              {isPartDay && formatTimeRange() && (
                <p className="text-xs text-muted-foreground mt-1">
                  {formatTimeRange()}
                </p>
              )}
            </div>
            {!isPartDay && (
              <div>
                <span className="text-sm text-muted-foreground">End Date</span>
                <p className="font-medium">
                  {format(new Date(request.end_date), 'MMM d, yyyy')}
                </p>
              </div>
            )}
            {isPartDay && (
              <div>
                <span className="text-sm text-muted-foreground">Hours</span>
                <p className="font-medium">
                  {request.requested_hours}h
                </p>
              </div>
            )}
          </div>

          {/* Duration & Status */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <span className="text-sm text-muted-foreground">Duration</span>
              <div className="flex items-center gap-2 mt-1">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium">
                  {formatDuration()}
                </span>
              </div>
            </div>
            <div>
              <span className="text-sm text-muted-foreground">Status</span>
              <div className="mt-1">
                <Badge variant={getStatusBadgeVariant(request.status)}>
                  {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                </Badge>
              </div>
            </div>
          </div>

          {/* Reason */}
          {request.reason && (
            <div>
              <span className="text-sm text-muted-foreground">
                {isPartDay ? 'Description' : 'Reason'}
              </span>
              <div className="flex items-start gap-2 mt-1">
                <FileText className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                <p className="text-sm min-w-0 break-words">{request.reason}</p>
              </div>
            </div>
          )}

          {/* Submitted Date & Actions */}
          <div className="pt-2 border-t">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">
                  Submitted: {format(new Date(request.created_at), 'MMM d, yyyy')}
                </span>
              </div>
              <LeaveRequestActions leaveRequest={request} />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
